import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../utils/api';
import { Client } from '../dtos/toneWebApp';
import { accessToken, getAuthDetails } from '../utils/helper';
import Cookies from 'universal-cookie';

const cookies = new Cookies(null, { path: '/' });

interface ClientState {
  clientList: { data: Client[]; totalPages: number } | { data: []; totalPages: 0 };
  loading: boolean;
  error: string | null;
  addClientResponse: { message: string };
  addClientError: string | null;
  updateClientResponse: { message: string };
  updateClientError: string | null;
  deleteClientResponse: { message: string };
  deleteClientError: string | null;
}

const initialState: ClientState = {
  clientList: { data: [], totalPages: 0 },
  addClientResponse: { message: '' },
  updateClientResponse: { message: '' },
  deleteClientResponse: { message: '' },
  loading: false,
  error: null,
  addClientError: null,
  updateClientError: null,
  deleteClientError: null,
};

interface AddClientResponse {
  message: string;
  icon?: any;
  background?: any;
}

interface UpdateClientResponse {
  message: string;
  icon?: any;
  background?: any;
}

export const getAllClientsTrigger = createAsyncThunk(
  '/client/get',
  async ({ page, limit, search }: { page?: number; limit?: number; search?: string }) => {
    try {
      const queryParams = new URLSearchParams();
      if (page) queryParams.append('page', page.toString());
      if (limit) queryParams.append('limit', limit.toString());
      if (search) queryParams.append('search', search);
      const response = await api.get(`/client?${queryParams}`, accessToken());
      return response.data;
    } catch (error) {
      console.log(error);
      return { data: [], totalPages: 0 };
    }
  },
);

export const addClientTrigger = createAsyncThunk('/client/add', async (client: FormData) => {
  try {
    const response = await api.post<AddClientResponse>('/client/add', client, {
      withCredentials: true,
      headers: {
        Authorization: `Bearer ${getAuthDetails().accessToken}`,
        'Content-Type': 'multipart/form-data',
        'x-csrf-token': cookies.get('XSRF-TOKEN'),
      },
    });
    return response.data;
  } catch (error) {
    console.log(error);
    return { message: '' };
  }
});

export const updateClientStatusTrigger = createAsyncThunk(
  '/client/update-status/:id',
  async ({ id, status }: { id: number; status: boolean }) => {
    try {
      const response = await api.patch<{ message: string }>(
        `/client/update-status/${id}`,
        { status },
        accessToken(),
      );
      return response.data;
    } catch (error) {
      console.log(error);
      return { message: '' };
    }
  },
);

export const updateClientOfflineModeTrigger = createAsyncThunk(
  '/client/update-offline-mode/:id',
  async ({ id, offlineMode }: { id: number; offlineMode: boolean }) => {
    try {
      const response = await api.patch<{ message: string }>(
        `/client/update-offline-mode/${id}`,
        { offlineMode },
        accessToken(),
      );
      return response.data;
    } catch (error) {
      console.log(error);
      return { message: '' };
    }
  },
);

export const updateClientTrigger = createAsyncThunk(
  '/client/update/:id',
  async ({ id, request }: { id: number; request: FormData }) => {
    try {
      const response = await api.patch<{ message: string, icon: File, background: File }>(
        `/client/update/${id}`,
        request,
        accessToken(),
      );
      return response.data;
    } catch (error) {
      console.log(error);
      return { message: '' };
    }
  },
);

export const deleteClientTrigger = createAsyncThunk('/client/delete/:id', async (id?: number) => {
  try {
    const response = await api.delete<{ message: string }>(
      `/client/delete/${id}`,
      accessToken(),
    );
    return response.data;
  } catch (error) {
    console.log(error);
    return { message: '' };
  }
});

// Create slice
const clientSlice = createSlice({
  name: 'client',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getAllClientsTrigger.pending, (state) => {
        state.loading = true;
      })
      .addCase(getAllClientsTrigger.fulfilled, (state, action) => {
        state.loading = false;
        state.clientList = action.payload;
      })
      .addCase(getAllClientsTrigger.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to load posts';
      })
      .addCase(addClientTrigger.pending, (state) => {
        state.loading = true;
      })
      .addCase(addClientTrigger.fulfilled, (state, action) => {
        state.loading = false;
        state.addClientResponse = action.payload;
      })
      .addCase(addClientTrigger.rejected, (state, action) => {
        state.loading = false;
        state.addClientError = action.error.message || 'Failed to load posts';
      })
      .addCase(updateClientTrigger.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateClientTrigger.fulfilled, (state, action) => {
        state.loading = false;
        state.updateClientResponse = action.payload;
      })
      .addCase(updateClientTrigger.rejected, (state, action) => {
        state.loading = false;
        state.updateClientError = action.error.message || 'Failed to load posts';
      })
      .addCase(deleteClientTrigger.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteClientTrigger.fulfilled, (state, action) => {
        state.loading = false;
        state.deleteClientResponse = action.payload;
      })
      .addCase(deleteClientTrigger.rejected, (state, action) => {
        state.loading = false;
        state.deleteClientError = action.error.message || 'Failed to load posts';
      });
  },
});

export default clientSlice.reducer;
